import React, { useEffect, useState } from "react";
import { FaSearch, FaStar, FaTimes } from "react-icons/fa";
import { FHOST } from "../constants/Functions";
import axios from "axios";

const TeacherProfiles = ({userInfo}) => {
  const initialTeachers = [
    {
      id: 1,
      name: "John Doe",
      profile_picture: "https://via.placeholder.com/150",
      current_school: "Springfield High School",
      subjects: ["Math", "Physics"],
      rating: 4.5,
      cost: 2000, // Example cost
      availability: [
        { date: "2024-11-27", time: "10:00 AM - 12:00 PM", isAvailable: true },
        { date: "2024-11-27", time: "2:00 PM - 4:00 PM", isAvailable: false },
      ],
    },
  ];

  const [teachers, setTeachers] = useState(initialTeachers);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    time: "",
    student_id: userInfo?.id, // Example student ID (should be dynamically set)
  });

  useEffect(() => {
    // Fetch teachers data from the backend
    const fetchTeachers = async () => {
      try {
        const response = await axios.get(`${FHOST}/users/api/teachers`); // Replace with your backend endpoint
        if (response.data.success) {
          // Format the data to include default values for rating and availability
          const formattedTeachers = response.data.teachers.map((teacher) => ({
            ...teacher,
            profile_picture: teacher.profilePicture || 'https://via.placeholder.com/50',
            registered_on: teacher.registered_on
              ? new Date(teacher.registered_on).toLocaleDateString()
              : 'Not Registered',
            rating: teacher.rating || 4.5, // Default rating for testing
            availability: teacher.availability || [
              {
                date: '2024-11-27',
                time: '9:00 AM - 11:00 AM',
                isAvailable: true,
              },
              {
                date: '2024-11-27',
                time: '3:00 PM - 5:00 PM',
                isAvailable: true,
              },
            ],
          }));
          setTeachers(formattedTeachers);
        } else {
          setError('Failed to fetch teachers');
        }
      } catch (err) {
        setError('An error occurred while fetching teachers data');
      } finally {
        setLoading(false);
      }
    };

    fetchTeachers();
  }, []);
  const handleTeacherClick = (teacher) => {
    setSelectedTeacher(teacher);
  };

  const handleBack = () => {
    setSelectedTeacher(null);
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;

    // Filter available times based on selected date
    const availableTimesForDate = selectedTeacher.availability
      .filter((slot) => slot.date === selectedDate && slot.isAvailable)
      .map((slot) => slot.time);

    setAvailableTimes(availableTimesForDate);
    setFormData({ ...formData, date: selectedDate, time: "" });
  };

  const handleTimeChange = (e) => {
    setFormData({ ...formData, time: e.target.value });
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.time) {
      alert("Please select both date and time.");
      return;
    }

    const selectedSlot = selectedTeacher.availability.find(
      (slot) => slot.date === formData.date && slot.time === formData.time
    );

    if (!selectedSlot || !selectedSlot.isAvailable) {
      alert("The selected time slot is not available.");
      return;
    }

    const bookingData = {
      teacher_id: selectedTeacher.id,
      student_id: userInfo?.id,
      date: formData.date,
      time: formData.time,
      cost: selectedTeacher.cost || 0,
    };

    try {
      const response = await axios.post(`${FHOST}/lessons/api/book-session`, bookingData);
      if (response.data.success) {
        setSuccessMessage("Session booked. Waiting for confirmation.");
        setIsScheduling(false);
      } else {
        alert("Failed to book session. Try again.");
      }
    } catch (error) {
      alert("Error booking session.");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {successMessage && (
        <div className="p-3 mb-4 bg-green-500 text-white rounded-lg">
          {successMessage}
        </div>
      )}

      {!selectedTeacher && (
        <div className="grid grid-cols-2 gap-4">
          {teachers.map((teacher) => (
            <div key={teacher.id} className="bg-white p-4 rounded-lg shadow-md cursor-pointer" onClick={() => handleTeacherClick(teacher)}>
              <img src={teacher.profile_picture} alt={teacher.name} className="w-full h-48 object-cover rounded-lg mb-4" />
              <h2 className="text-lg font-bold">{teacher.name}</h2>
              <p className="text-gray-600">{teacher.current_school}</p>
              <p className="text-gray-600">Subjects: {teacher.subjects.join(", ")}</p>
            </div>
          ))}
        </div>
      )}

      {selectedTeacher && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <button onClick={handleBack} className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg">Back</button>
          <h2 className="text-2xl font-bold">{selectedTeacher.name}</h2>
          <p className="text-gray-600">{selectedTeacher.current_school}</p>
          <p className="text-gray-600">Subjects: {selectedTeacher.subjects.join(", ")}</p>
          <p>Cost: KES {selectedTeacher.cost || 0}</p>
          <button onClick={() => setIsScheduling(true)} className="px-4 py-2 bg-blue-500 text-white rounded-lg">Schedule Lesson</button>

          <h3 className="text-xl font-bold mt-4">Availability</h3>
          <ul className="list-disc list-inside text-gray-600">
            {selectedTeacher.availability.map((slot, index) => (
              <li key={index}>
                {slot.date} - {slot.time} {slot.isAvailable ? <span className="text-green-500">Available</span> : <span className="text-red-500">Not Available</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isScheduling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Schedule Lesson</h2>
              <button onClick={() => setIsScheduling(false)} className="text-red-500"><FaTimes /></button>
            </div>

            <form onSubmit={handleScheduleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">Select Date:</label>
                <select value={formData.date} onChange={handleDateChange} className="w-full p-2 border rounded-lg">
                  <option value="">Select Date</option>
                  {[...new Set(selectedTeacher.availability.map(slot => slot.date))].map((date) => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">Select Time:</label>
                <select value={formData.time} onChange={handleTimeChange} className="w-full p-2 border rounded-lg">
                  <option value="">Select Time</option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              <button type="submit" className="w-full px-4 py-2 bg-green-500 text-white rounded-lg">Confirm Booking</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherProfiles;
